import { api } from '@/config/api';

export interface ApiKeySummary {
  apiKeyUid: string;
  name: string;
  prefix: string;
  scopes: string[];
  isActive: boolean;
  lastUsedAt?: string;
  totalRequests: number;
  expiresAt?: string;
  revokedAt?: string;
  createdAt: string;
}

export interface CreateApiKeyResult extends Omit<ApiKeySummary, 'lastUsedAt' | 'totalRequests' | 'isActive'> {
  /** Plaintext key — shown ONCE. Never returned again. */
  plaintextKey: string;
}

export interface WebhookSubscription {
  subscriptionUid: string;
  name: string;
  url: string;
  events: string[];
  isActive: boolean;
  lastDeliveryAt?: string;
  lastStatusCode?: number;
  lastError?: string;
  totalDeliveries: number;
  totalFailures: number;
  createdAt: string;
}

export interface CreateWebhookResult extends Omit<WebhookSubscription, 'lastDeliveryAt' | 'lastStatusCode' | 'lastError' | 'totalDeliveries' | 'totalFailures'> {
  /** Plaintext shared secret — shown ONCE. Used by the receiver to verify the HMAC. */
  secret: string;
}

const KEYS = '/slighter_crm/api-keys';
const HOOKS = '/slighter_crm/webhooks';

export const developerService = {
  // ─── API Keys ───────────────────────────────────────────────────────────
  listApiKeys: async (): Promise<ApiKeySummary[]> => (await api.get(KEYS)).data,
  listScopes: async (): Promise<{ scopes: string[] }> => (await api.get(`${KEYS}/scopes`)).data,
  createApiKey: async (body: { name: string; scopes: string[]; expiresAt?: string }): Promise<CreateApiKeyResult> =>
    (await api.post(KEYS, body)).data,
  revokeApiKey: async (apiKeyUid: string): Promise<void> => (await api.delete(`${KEYS}/${apiKeyUid}`)).data,

  // ─── Webhooks ──────────────────────────────────────────────────────────
  listWebhooks: async (): Promise<WebhookSubscription[]> => (await api.get(HOOKS)).data,
  listEvents: async (): Promise<{ events: string[] }> => (await api.get(`${HOOKS}/events`)).data,
  createWebhook: async (body: { name: string; url: string; events: string[] }): Promise<CreateWebhookResult> =>
    (await api.post(HOOKS, body)).data,
  updateWebhook: async (uid: string, body: Partial<{ name: string; url: string; events: string[]; isActive: boolean }>): Promise<void> =>
    (await api.patch(`${HOOKS}/${uid}`, body)).data,
  deleteWebhook: async (uid: string): Promise<void> => (await api.delete(`${HOOKS}/${uid}`)).data,
  testWebhook: async (uid: string): Promise<{ delivered: boolean; status?: number; error?: string }> =>
    (await api.post(`${HOOKS}/${uid}/test`)).data,
};
